import { forwardRef, useRef, useState } from 'react'
import { merge } from 'remeda'
import { FreeMode, Mousewheel, Navigation, type Swiper } from 'swiper'
import { SwiperSlide } from 'swiper/react'
import { useEventListener } from 'usehooks-ts'
import { ApparelProduct } from '@components/ApparelProduct/ApparelProduct'
import { dataTestIds } from '@velasca/constants'
import { NavigationButton } from '@velasca/ui/navigation-button'
import {
  Container,
  HeadingsContainer,
  LeftArrowContainer,
  RightArrowContainer,
  StyledSwiper,
  Title,
} from './ProductsSlider.styles'
import type { ProductsSliderProps } from './ProductsSlider.types'

const getSpaceBetween = () => {
  return { desktop: 10, mobile: 6 }
}

export const ProductsSlider = forwardRef<HTMLDivElement, ProductsSliderProps>(
  (props, sliderRef) => {
    const {
      title,
      onQuickAdd,
      loadingSlides = 6,
      showProductType = false,
      arrowVisible = false,
      className = '',
      sliderStyles,
      breakpoints,
      loop = false,
      loading = false,
      products,
      onAddToCart,
      dataTestId,
      allowQuickAdd = true,
    } = props
    const navigationPrevRef = useRef(null)
    const navigationNextRef = useRef(null)
    const [slider, setSlider] = useState<Swiper | undefined>()

    const navigate = (e: KeyboardEvent) => {
      switch (e.code) {
        case 'ArrowRight': {
          slider?.slideNext()
          break
        }
        case 'ArrowLeft': {
          slider?.slidePrev()
          break
        }
        default: {
          return
        }
      }
    }

    useEventListener('keydown', navigate)

    return (
      <Container
        ref={sliderRef}
        className={className}
        data-testid={dataTestIds.GENERIC_PRODUCTS_SLIDER}
      >
        {title && (
          <HeadingsContainer>
            {title && <Title>{title}</Title>}
          </HeadingsContainer>
        )}
        <StyledSwiper
          freeMode
          loop={loop}
          style={sliderStyles}
          slidesPerView={1.2}
          mousewheel={{ forceToAxis: true }}
          spaceBetween={getSpaceBetween().mobile}
          modules={[Navigation, FreeMode, Mousewheel]}
          data-testid={dataTestId}
          navigation={{
            prevEl: navigationPrevRef.current,
            nextEl: navigationNextRef.current,
          }}
          breakpoints={merge(
            {
              768: {
                spaceBetween: getSpaceBetween().desktop,
                slidesPerView: 2.1,
              },
              992: {
                spaceBetween: getSpaceBetween().desktop,
                slidesPerView: 3.4,
              },
            },
            breakpoints,
          )}
          onSwiper={setSlider}
          onBeforeInit={(swiper) => {
            const { navigation } = swiper.params

            if (navigation && typeof navigation === 'object') {
              navigation.prevEl = navigationPrevRef.current
              navigation.nextEl = navigationNextRef.current
            }
          }}
        >
          {arrowVisible && (
            <LeftArrowContainer>
              <NavigationButton
                buttonType="prev"
                onClick={() => {
                  slider?.slidePrev()
                }}
              />
            </LeftArrowContainer>
          )}
          {loading
            ? [...Array(loadingSlides).keys()].map((id) => (
                <SwiperSlide key={id}>
                  <ApparelProduct loading />
                </SwiperSlide>
              ))
            : products?.map(
                ({
                  id,
                  productType,
                  title: productTitle,
                  variants,
                  sizes,
                  isGiftCard,
                  collectionBrandID,
                  storeExclusive,
                }) => {
                  return (
                    <SwiperSlide key={id}>
                      <ApparelProduct
                        showSeason
                        isQuickAdd={allowQuickAdd}
                        showProductType={showProductType}
                        productType={productType}
                        title={productTitle}
                        variants={variants}
                        defaultSelected={0}
                        sizes={sizes}
                        isGiftCard={isGiftCard}
                        collectionBrandID={collectionBrandID}
                        storeExclusive={storeExclusive}
                        showVariantSelector={false}
                        onQuickAdd={onQuickAdd}
                        onAddToCart={onAddToCart}
                      />
                    </SwiperSlide>
                  )
                },
              )}
          {arrowVisible && (
            <RightArrowContainer>
              <NavigationButton
                buttonType="next"
                onClick={() => {
                  slider?.slideNext()
                }}
              />
            </RightArrowContainer>
          )}
        </StyledSwiper>
      </Container>
    )
  },
)
